import axios from "axios"
import { toast } from "sonner"

declare module "axios" {
  export interface AxiosRequestConfig {
    skipErrorToast?: boolean
  }
}

// ── Extract exact message from various backend error shapes ────────────────────
export function extractErrorMessage(data: any, fallback: string = "An unexpected error occurred"): string {
  if (!data) return fallback
  if (typeof data === "string" && data.trim()) return data
  if (Array.isArray(data.message) && data.message.length > 0) {
    return data.message.filter(Boolean).join(". ")
  }
  if (typeof data.message === "string" && data.message.trim()) {
    return data.message
  }
  if (typeof data.error === "string" && data.error.trim()) {
    return data.error
  }
  if (typeof data.error?.message === "string" && data.error.message.trim()) {
    return data.error.message
  }
  if (typeof data.detail === "string" && data.detail.trim()) {
    return data.detail
  }
  return fallback
}

// ── Axios instance ──────────────────────────────────────────────────────────────
export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
})

// ── Custom error class (preserves backend error code) ───────────────────────────
export class ApiError extends Error {
  public readonly code?: string;
  public readonly status?: number;
  public readonly data?: any;

  constructor(
    message: string,
    code?: string,
    status?: number,
    data?: any
  ) {
    super(message)
    this.name = "ApiError"
    this.code = code;
    this.status = status;
    this.data = data;
  }
}

// ── Token Refresh Interceptor ────────────────────────────────────────────────
// Tracks whether a refresh is in-flight so parallel 401s don't trigger multiple refreshes
let isRefreshing = false
let failedQueue: Array<{
  resolve: (token: string) => void
  reject: (err: unknown) => void
}> = []

function processQueue(error: unknown, token: string | null) {
  failedQueue.forEach((p) => {
    if (error) p.reject(error)
    else p.resolve(token!)
  })
  failedQueue = []
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // If request was canceled by AbortController/Signal, silently reject
    if (axios.isCancel(error) || error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
      return Promise.reject(error)
    }

    const originalRequest = error.config

    // Skip refresh logic for authentication endpoints (sign-in, sign-up, refresh)
    const isAuthEndpoint =
      originalRequest?.url?.includes("/auth/sign-in") ||
      originalRequest?.url?.includes("/auth/sign-up") ||
      originalRequest?.url?.includes("/auth/refresh")

    // Only attempt refresh on 401 responses that haven't already been retried
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      // Lazily import auth store to avoid circular dep at module init time
      const { useAuthStore } = await import("@/store/auth.store")
      const { accessToken, refreshToken, setAuth, clearAuth } = useAuthStore.getState()

      if (!refreshToken) {
        // No refresh token stored — this is an anonymous visitor or a cleared session.
        // Don't force them to /login mid-browse; public search endpoints just reject.
        clearAuth()
        if (accessToken) {
          window.location.href = "/login"
        }
        return Promise.reject(error)
      }

      if (isRefreshing) {
        // Another refresh is already in progress — queue this request
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then((newToken) => {
            originalRequest.headers["Authorization"] = `Bearer ${newToken}`
            return apiClient(originalRequest)
          })
          .catch((err) => Promise.reject(err))
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        // Use raw axios (NOT apiClient) to bypass this interceptor and avoid infinite loops
        const rawAxios = (await import("axios")).default
        const baseURL = import.meta.env.VITE_API_URL
        const { data: envelope } = await rawAxios.post(
          `${baseURL}/auth/refresh`,
          { refreshToken },
          { headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" } }
        )
        const newAccessToken: string = envelope.data.accessToken
        const newRefreshToken: string = envelope.data.refreshToken

        // Update store with new tokens (preserve existing user object)
        const currentState = useAuthStore.getState()
        if (currentState.user) {
          setAuth({
            accessToken: newAccessToken,
            refreshToken: newRefreshToken,
            user: currentState.user,
          })
        }

        // Update default header and retry original request
        apiClient.defaults.headers.common["Authorization"] = `Bearer ${newAccessToken}`
        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`
        processQueue(null, newAccessToken)
        return apiClient(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        // Import store again for clearAuth since this is a different scope
        const { useAuthStore: store } = await import("@/store/auth.store")
        store.getState().clearAuth()
        window.location.href = "/login"
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    // Normalize all other errors to ApiError so callers can inspect `code` field
    const status = error.response?.status as number | undefined
    const message = extractErrorMessage(
      error.response?.data,
      error.message ?? "An unexpected error occurred"
    )
    const code = (error.response?.data?.code ?? error.response?.data?.errorCode) as string | undefined

    // Display backend's exact message in toaster for all 4xx/5xx errors unless explicitly skipped
    if (!originalRequest?.skipErrorToast) {
      if (status && status >= 400 && status < 600) {
        toast.error(message, { id: message })
      } else if (!status && error.message && error.message !== "canceled") {
        toast.error(message, { id: message })
      }
    }

    return Promise.reject(new ApiError(message, code, status, error.response?.data))
  }
)



// ── Shared response types ───────────────────────────────────────────────────────
export interface EmployeeProfile {
  id: string
  userId: string
  companyId: string | null
  designation: string
  department: string
  phoneNumber: string
  bio: string
  verificationStatus: string
}

export interface AuthUser {
  id: string
  name: string
  email: string
  accountType: string
  role?: "BUYER" | "SELLER" | "ADMIN"
  emailVerified: boolean
  profileImageUrl: string | null
  createdAt: string
  buyer: null | any
  employeeProfile: EmployeeProfile | null
}

// The backend wraps everything in:
// { success, statusCode, message, data: { user, accessToken, refreshToken }, meta }
export interface AuthResponseEnvelope {
  success: boolean
  statusCode: number
  message: string
  data: {
    user: AuthUser
    accessToken: string
    refreshToken: string
  }
}

// Convenience flat shape that the rest of the app works with
export interface AuthResponse {
  accessToken: string
  refreshToken: string
  user: AuthUser
}
